import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { getToken } from "firebase/messaging";
import axios from "axios";

import { exitFullscreen } from "../kiosk/component/function/kiosk";
import { messaging } from "../../../firebase/firebase.js";
import { useDisableScroll } from "../../../hooks/useDisableScroll";

import Pickup from "./pickup";
import SalesCheck from "./salesCheck";
import Review from "./review";
import Setting from "./setting";
import Menus from "./menus";
import StoreHeader from "./component/common/storeHeader";
import { AlertModal, ChoiceModal, ConfirmationModal, SelectModal } from "./component/common/modal";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { Container } from "./component/styled/common.js";

const defaultModalConfig = {
  isOpen: false,
  type: "",
  title: "",
  message: [],
  handleConfirm: null,
  handleCancel: null,
};

const Store = () => {
  useDisableScroll();
  const eventSourceRef = useRef(null);
  const timerRef = useRef(null);

  const navigate = useNavigate();

  const isMobile = useMediaQuery({ query: "(max-width:767px)" });

  const companies = useSelector((state) => state?.company?.data);
  const stores = companies.filter((item) => item.gbn === "ss" || item.cmpnyNo === 16421) || [];
  const [currentAuth, setCurrentAuth] = useState(""); // VIEWER

  const [activeMenu, setActiveMenu] = useState("");
  const [data, setData] = useState([]);
  const [checkedOrders, setCheckedOrders] = useState();
  const [mobileActive, setMobileActive] = useState("PENDING");
  const [desktopActive, setDesktopActive] = useState("PENDING");
  const [alertModalConfigs, setAlertModalConfigs] = useState([]);
  const [modalConfig, setModalConfig] = useState({ ...defaultModalConfig });
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectCompany, setSelectCompany] = useState();
  const [settingActive, setSettingActive] = useState("menuBoard");
  const [company, setCompany] = useState(null);

  useEffect(() => {
    setModal(getModalContent());
  }, [modalConfig]);

  useEffect(() => {
    if (!selectCompany) return;

    getOrders(selectCompany);
    getLocalStorage(selectCompany);
    getCompany(selectCompany);

    setCurrentAuth(stores.find((cmpny) => cmpny.cmpnyNo === selectCompany.value)?.auth);

    const eventSource = initializeOrderEvent(selectCompany);
    return () => {
      eventSource.close();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [selectCompany]);

  useEffect(() => {
    if (!stores || !stores.length) {
      exitFullscreen();
      toast.error("회사가 존재하지 않습니다.");
      navigate("/mypage");
    }

    const store = JSON.parse(sessionStorage.getItem("store"));
    if (store) {
      setSelectCompany(store);
      const hashValue = window.location.hash.replace("#", "");
      if (hashValue) {
        setActiveMenu(hashValue);
      }
    } else {
      if (stores.length === 1) {
        setSelectCompany({ value: stores[0].cmpnyNo, label: stores[0].cmpnyNm });
      } else {
        openModal({
          type: "select",
          title: "회사 선택",
          placeholder: "회사를 선택해주세요.",
          handleChangeOption: (option) => changeOption(option),
          options: [...stores].map((item) => ({ value: item.cmpnyNo, label: item.cmpnyNm })),
          handleConfirm: (reason) => {
            if (!reason) {
              toast.error("선택된 회사가 존재하지 않습니다.");
              return;
            }
            setSelectCompany(reason);
            sessionStorage.setItem("store", JSON.stringify(reason));
            closeModal(true);
          },
          required: true,
        });
      }
    }
  }, []);

  const handleReConnection = () => {
    console.log("재시도란다");
    const store = JSON.parse(sessionStorage.getItem("store"));

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      initializeOrderEvent(store);
    }, 5000);
  };

  const getModalContent = () => {
    if (!modalConfig.isOpen) return null;

    const modalProps = {
      title: modalConfig.title,
      message: modalConfig.message,
      handleConfirm: modalConfig.handleConfirm || closeModal,
      handleCancel: modalConfig?.required ? null : modalConfig.handleCancel || closeModal,
      ...modalConfig,
    };

    switch (modalConfig.type) {
      case "alert":
        return <AlertModal {...modalProps} />;
      case "confirmation":
        return <ConfirmationModal {...modalProps} />;
      case "select":
        return <SelectModal {...modalProps} active={modalConfig.active} />;
      default:
        return null;
    }
  };

  // 회사 조회
  const getCompany = async (cmpny) => {
    const url = "/erp/ss/company/get";
    const body = { cmpnyNo: cmpny.value };
    try {
      const res = await axios.post(url, body);
      if (res.status === 200) {
        setCompany(res.data);
        console.log(res.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getEncryptId = async (id) => {
    const url = "/etc/encrypt-6char";
    const body = { id: parseInt(id, 10) };
    const res = await axios.post(url, body);
    return res.data.code;
  };

  // 웹소켓 연결
  const initializeOrderEvent = (company) => {
    const eventSource = new EventSource(`https://erp.1472.ai/events?cmpnyNo=${company.value}`);
    // const eventSource = new EventSource(
    //   `http://192.168.219.44:8888/events?cmpnyNo=${company.value}`,
    // );
    eventSourceRef.current = eventSource;

    eventSource.onopen = async () => {
      console.log("eventSource open");
    };

    eventSource.onerror = (error) => {
      console.error("알림 에러:", error);
      eventSource.close();
      handleReConnection();
    };

    eventSource.onmessage = async (event) => {
      let token;
      let message = JSON.parse(event.data);
      if (message.status === "connected") return;

      const encryptId = await getEncryptId(message.orderId);
      const messages = {
        OC: { title: "신규 주문", body: `${message.message}이 들어왔습니다.`, tab: "PENDING" },
        OCC: {
          title: "주문 취소",
          body: `주문번호 ${encryptId}이 취소되었습니다.`,
          tab: "PICKUP",
        },
        ORC: {
          title: "주문 취소 요청",
          body: `주문번호 ${encryptId}이 취소 요청이 들어왔습니다.`,
          tab: "PENDING",
        },
      };

      try {
        token = await getToken(messaging, {
          vapidKey:
            "BDTF9CMkogFUR-U1aUldUuwl1y3c6V9UuEYtcWoCBMXFJUj9bXOOJpsb249HJcUU4SM8j6RG5zwVe0hrUIzhNQU",
        });
      } catch (error) {
        console.error("FCM Token err:", error);
      }

      getOrders(company);

      if (Notification.permission === "granted") {
        const notification = new Notification(`${messages[message?.gbn]?.title} `, {
          body: `${messages[message?.gbn]?.body} 주문을 확인해 주세요.`,
          icon: "/img/logo.png",
        });

        notification.onclick = function () {
          window.focus();
          selectOrder({ id: message?.orderId });
          setActiveMenu("pickup");
        };

        const sound = new Audio("/source/notification.mp3");
        sound.play().catch((error) => {
          console.error("사운드 재생 오류:", error);
        });

        if (isMobile) {
          setMobileActive(messages[message?.gbn]?.tab);
        } else {
          setDesktopActive(messages[message?.gbn]?.tab);
        }
      } else {
        console.warn("알림 권한이 거부되었습니다.");
      }

      openAlertModal({
        orderId: message.orderId,
        title: `${messages[message?.gbn]?.title}`,
        message: [`${messages[message?.gbn]?.body}`, "주문을 확인해 주세요."],
      });
    };

    return eventSource;
  };

  const selectOrder = async (item) => {
    const detail = await getOrder(item.id);

    if (detail) {
      updateLocalStorage(detail.id);
      setSelected({
        ...detail,
        orderId: item.orderId ? item.orderId : await getEncryptId(item.id),
      });
    }
  };

  const updateLocalStorage = (id) => {
    setCheckedOrders((orders) => {
      const cmpnyNo = selectCompany?.value;
      if (!cmpnyNo) {
        return orders;
      }

      const currentOrders = orders[cmpnyNo] || [];

      if (!currentOrders.includes(id)) {
        const updatedOrders = [...currentOrders, id];

        const updatedState = {
          ...orders,
          [cmpnyNo]: updatedOrders,
        };

        localStorage.setItem("checkedOrders", JSON.stringify(updatedState));

        return updatedState;
      }

      return orders;
    });
  };

  // 주문 상세보기
  const getOrder = async (id) => {
    if (selected?.id === id) return;
    const query = `
      query SsOrder($id: ID!) {
        ssOrder(id: $id) {
            id
            orderFlag
            mberNo
            mberNm
            mberTel
            cmpnyNo
            cmpnyNm
            reqTerm
            price
            useCouponID
            couponPrice
            totalPrice
            status
            createdAt
            updatedAt
            pickupBy
            cancelReason
            tableNo
            paymentType
            products {
              id
              orderId
              productId
              productImgSrc
              productName
              quantity
              price
                options {
                id
                orderProductId
                optionId
                optionName
                optionPrice
            }
          }
        }
      }
    `;
    const variables = { id };

    try {
      const res = await axios.post("/erp/ss/order", { query, variables });
      return res?.status === 200 ? res.data : null;
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  // 주문 리스트 조회
  const getOrders = async (company) => {
    const query = `
    query SsOrders($cmpnyNo: Int, $createdAt: String) {
      ssOrders(filter: { cmpnyNo: $cmpnyNo, createdAt: $createdAt }) {
       orders {
            id
            orderFlag            
            status
            createdAt
            pickupBy
            paymentType
            price
            tableNo
        }
      }
    }
  `;

    const variables = {
      cmpnyNo: company?.value,
      createdAt: moment().format("YYYY-MM-DD"),
    };

    try {
      const res = await axios.post("/erp/ss/orders", { query, variables });
      const orders = res?.data?.orders || [];

      const encryptedOrders = await Promise.all(
        orders.map(async (order) => ({
          ...order,
          orderId: await getEncryptId(order.id),
        })),
      );

      setData(encryptedOrders);
    } catch (error) {
      console.error(error);
    }
  };
  const getLocalStorage = (company) => {
    let local = JSON.parse(localStorage.getItem("checkedOrders"));
    if (!local || typeof local !== "object" || Array.isArray(local)) {
      local = {};
    }

    if (!local.hasOwnProperty(company.value)) {
      local[company.value] = [];
    }

    setCheckedOrders(local);
  };

  const openModal = (options) => {
    setModalConfig({
      isOpen: true,
      ...options,
    });
  };

  const closeModal = (flag = false) => {
    setModalConfig(() => ({ ...defaultModalConfig }));

    if (flag) {
      setSelected(null);
    }
  };

  const changeOption = (item) => {
    setModalConfig((config) => ({ ...config, active: item }));
  };

  // 주문 알림 모달
  const closeAlertModal = (index = null) => {
    const orderId = alertModalConfigs[index].orderId;
    if (selected && selected?.id !== orderId) {
      selectOrder({ id: orderId });
    }

    setAlertModalConfigs((prevConfigs) => {
      if (index !== null) {
        return prevConfigs.filter((_, i) => i !== index);
      }
      return [];
    });
    setActiveMenu("pickup");
  };

  // 주문 및 취소 알림 모달
  const openAlertModal = (options) => {
    setAlertModalConfigs((config) => {
      const newIndex = config.length;
      return [
        ...config,
        {
          isOpen: true,
          type: options.type || "alert",
          ...options,
          handleConfirm: options.handleConfirm
            ? () => {
                options.handleConfirm();
                closeAlertModal(newIndex);
              }
            : () => closeAlertModal(newIndex),
          handleCancel: options.handleCancel
            ? () => {
                options.handleCancel();
                closeAlertModal(newIndex);
              }
            : null,
        },
      ];
    });
  };

  // 주문 모달 포지션 계산
  const calcAlertPosition = (index, totalModals) => {
    const basePosition = 50;
    const baseOffset = 10;

    if (totalModals === 1) {
      return {
        top: `${basePosition}%`,
        left: `${basePosition}%`,
        transform: "translate(-50%, -50%)",
      };
    }

    const offset = baseOffset * index;

    return {
      top: `calc(${basePosition}% + ${offset}px)`,
      left: `calc(${basePosition}% + ${offset}px)`,
    };
  };

  const renderComponent = () => {
    switch (activeMenu) {
      case "pickup":
        return (
          <Pickup
            getOrders={getOrders}
            data={data}
            checkedOrders={checkedOrders}
            mobileActive={mobileActive}
            setMobileActive={setMobileActive}
            desktopActive={desktopActive}
            setDesktopActive={setDesktopActive}
            closeModal={closeModal}
            openModal={openModal}
            selected={selected}
            setSelected={setSelected}
            changeOption={changeOption}
            selectOrder={selectOrder}
            selectCompany={selectCompany}
          />
        );
      case "salesCheck":
        return <SalesCheck selectCompany={selectCompany} />;
      case "review":
        return <Review selectCompany={selectCompany} />;
      case "setting":
        return (
          <Setting
            selectCompany={selectCompany}
            openModal={openModal}
            closeModal={closeModal}
            stores={stores}
            active={settingActive}
            setActive={setSettingActive}
            getCompany={getCompany}
            currentAuth={currentAuth}
            company={company}
          />
        );
      default:
        return <Menus setActiveMenu={setActiveMenu} company={company} />;
    }
  };

  const closeComponent = () => {
    if (activeMenu === "setting" && settingActive !== "menuBoard") {
      setSettingActive("menuBoard");
    } else {
      setActiveMenu(null);
      history.replaceState(null, "", window.location.pathname);
      setSettingActive("menuBoard");
    }
  };

  return (
    <Container>
      {alertModalConfigs.map((config, index) => {
        if (!config.isOpen) return null;

        const modalStyle = calcAlertPosition(index, alertModalConfigs.length);
        const commonProps = {
          key: index,
          title: config.title,
          message: config.message,
          index,
          style: modalStyle,
        };

        if (config.type === "confirmation") {
          return (
            <ConfirmationModal
              {...commonProps}
              handleConfirm={() => {
                config?.handleConfirm?.();
                closeAlertModal(index);
              }}
              handleCancel={() => {
                config?.handleCancel?.();
                closeAlertModal(index);
              }}
            />
          );
        }

        return <AlertModal {...commonProps} handleConfirm={() => closeAlertModal(index)} />;
      })}
      {modalConfig?.isOpen && modal}
      {activeMenu && (
        <StoreHeader
          selectCompany={selectCompany}
          setSelectCompany={setSelectCompany}
          stores={stores}
          openModal={openModal}
          closeModal={closeModal}
          closeComponent={closeComponent}
          company={company}
          getCompany={getCompany}
        />
      )}
      {renderComponent()}
    </Container>
  );
};

export default Store;
